/**
 * Identifies the current legacy support page and logs its Support V2
 * equivalent URL. Shared by page boot, pageshow (bfcache restore), and
 * in-app tab/route changes so the identification logic lives in one place.
 */

import { lang } from '../../common/page';
import { getSelectedOrg } from './support-org-util';

export const PORTAL_V2_URL_MAP = Object.freeze({
  home: '/support/v2/{lang}/',
  'my-cases': '/support/v2/{lang}/my-cases?myCase&orgId={orgId}',
  'my-org-cases': '/support/v2/{lang}/my-cases?myOrgCase&orgId={orgId}',
  'case-details': '/support/v2/{lang}/my-cases?myCase&caseId={caseId}&orgId={orgId}',
  'my-success': '/support/v2/{lang}/my-success',
  'manage-subscriptions': '/support/v2/{lang}/manage-subscriptions',
  'open-case': '/support/v2/{lang}/open-case',
  'create-subscription': '/support/v2/{lang}/create-subscription?edit={edit}',
  'offline-form': '/support/v2/{lang}/offline-form',
  unauthorized: '/support/v2/{lang}/unauthorized',
  error: '/support/v2/{lang}/error',
  'api-not-found': '/support/v2/{lang}/api-not-found',
  'internal-server-error': '/support/v2/{lang}/internal-server-error',
  'service-unavailable': '/support/v2/{lang}/service-unavailable',
  forbidden: '/support/v2/{lang}/forbidden',
  'page-not-found': '/support/v2/{lang}/page-not-found',
});

// Static destinations identified from the last URL path segment alone —
// no `support-tab` query param involved for these.
const PATH_IDENTIFIED_PAGES = new Set([
  'my-success',
  'manage-subscriptions',
  'open-case',
  'create-subscription',
  'unauthorized',
  'error',
  'api-not-found',
  'internal-server-error',
  'service-unavailable',
  'forbidden',
  'page-not-found',
]);

// A mapped-URL placeholder (e.g. {caseId}) may be sourced from one of these
// query param names on the current (legacy) URL, in preference order.
const PARAM_SOURCES = {
  caseId: ['case-id', 'caseId'],
  statusEventId: ['statusEventId', 'status-event-id'],
  edit: ['edit'],
};

/**
 * Resolves a single {placeholder} token the same way `modernSupportV2Href`
 * does — `lang` and `orgId` come from the app's own state (document lang /
 * selected-org cookie), everything else falls back to the current URL's
 * query params.
 */
function resolvePlaceholder(name, searchParams) {
  if (name === 'lang') return lang || '';
  if (name === 'orgId') return getSelectedOrg()?.id || searchParams.get('org-id') || '';

  const sources = PARAM_SOURCES[name] || [name];
  return sources.map((source) => searchParams.get(source)).find((value) => value) || '';
}

/**
 * Identifies the current page from the URL path/query params. Returns null
 * for anything unrecognized instead of throwing, so callers can no-op.
 */
export function identifyCurrentPage(pathname, searchParams) {
  const lastSegment = pathname.split('/').filter(Boolean).pop() || '';

  if (PATH_IDENTIFIED_PAGES.has(lastSegment)) {
    return lastSegment;
  }

  // Deep links (e.g. case-notification emails) carry `case-id`/`my-case-filter`
  // without a `support-tab` query param, so these must be checked independently
  // of `support-tab` — otherwise they fall through to the 'home' default below.
  if (searchParams.has('case-id')) return 'case-details';
  const caseFilter = searchParams.get('my-case-filter') || '';
  if (caseFilter) return caseFilter.startsWith('org-') ? 'my-org-cases' : 'my-cases';

  const supportTab = searchParams.get('support-tab') || 'home';

  if (supportTab === 'my-cases') return 'my-cases';
  if (supportTab === 'open-ticket') return 'open-case';
  if (supportTab === 'offline-form') return 'offline-form';
  if (supportTab === 'home') return 'home';

  return null;
}

/**
 * Fills a mapped URL template's {placeholder} tokens — `{lang}` in the path,
 * `{orgId}`/`{caseId}`/etc. in the query — using the same resolution
 * `modernSupportV2Href` relies on, then resolves it against the current
 * origin the same way. Query segments whose value can't be resolved are
 * dropped so a missing param never breaks the resulting URL.
 */
export function buildMappedUrl(template, searchParams) {
  if (!template) return '';

  const [base, query = ''] = template.split('?');
  const filledBase = base.replace(/\{(\w+)\}/g, (_match, name) => resolvePlaceholder(name, searchParams));

  if (!query) {
    return new URL(filledBase, window.location.origin).href;
  }

  const filledSegments = query
    .split('&')
    .map((segment) => {
      const [key, placeholder] = segment.split('=');
      if (!placeholder) return key; // static flag, e.g. `myCase`
      const match = placeholder.match(/^\{(\w+)\}$/);
      if (!match) return `${key}=${placeholder}`;
      const value = resolvePlaceholder(match[1], searchParams);
      return value ? `${key}=${encodeURIComponent(value)}` : null;
    })
    .filter(Boolean);

  const finalPath = filledSegments.length ? `${filledBase}?${filledSegments.join('&')}` : filledBase;
  return new URL(finalPath, window.location.origin).href;
}

let lastLoggedNavigationKey = null;

/**
 * Redeem-code links must remain on the legacy support page because Support V2
 * has no equivalent route and the query parameter contains the code to redeem.
 */
export function isRedeemCodeDeepLink(searchParams = new URLSearchParams(window.location.search)) {
  return searchParams.get('support-tab') === 'redeem-code' && searchParams.has('code');
}

/**
 * Runs on every page open / in-app route change. Only acts when the "new
 * homepage" toggle is active or the page was just restored from bfcache
 * (`pageshow` with `persisted: true`); no-ops otherwise. Safe to call from
 * multiple call sites for the same navigation — it logs at most once per
 * distinct URL (path + query).
 */
export function portalPageMapping({ toggleContainer, persisted = false } = {}) {
  const isActive = toggleContainer?.classList?.contains('is-active') ?? false;
  if (!isActive && !persisted) return;

  const { pathname, search } = window.location;
  const searchParams = new URLSearchParams(search);
  if (isRedeemCodeDeepLink(searchParams)) return;

  const navigationKey = `${pathname}${search}`;
  if (navigationKey === lastLoggedNavigationKey) return;
  lastLoggedNavigationKey = navigationKey;

  const currentPage = identifyCurrentPage(pathname, searchParams);
  const mappedUrl = currentPage ? PORTAL_V2_URL_MAP[currentPage] : null;
  const finalMappedUrl = mappedUrl ? buildMappedUrl(mappedUrl, searchParams) : null;

  console.log('[support-v2-mapping]', {
    currentPage: currentPage || 'unmapped',
    currentPath: pathname,
    currentQueryParams: Object.fromEntries(searchParams.entries()),
    mappedUrl,
    finalMappedUrl,
  });
  if (!finalMappedUrl) return;

  window.location.href = finalMappedUrl;
}
